import { useTranslation } from 'react-i18next';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { Button } from 'core/components';
import { AccessPermissions } from 'core/enums';
import { FilenetStatus } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { IVendorQuote } from 'quotes/types';
import { Color, Size, TooltipPosition, Variant } from 'theme/enums';

interface IProps {
  quote: IVendorQuote;
}

export const SendToFilenetButton = ({ quote }: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const { sendVendorQuoteToFilenet, isSendVendorQuoteToFilenetLoading } = useQuoteActions();

  // Whether the button renders at all is the role-resource gate's call (PR #1356). An unconfigured
  // company keeps it visible-but-blocked, so the user gets an actionable reason instead of a
  // silently missing action.
  const isIntegrationMissing = !user?.companySettings?.filenetEnabled;

  const isAlreadySent = FilenetStatus.SENT === quote.filenetStatus;

  // Deliberately NOT disabled while sending: a crashed send's stale claim is only taken over by
  // the next click. A redundant click is safe — the server-side claim rejects it.
  const isSendInProgress = FilenetStatus.SENDING === quote.filenetStatus;

  const onClick = () => {
    sendVendorQuoteToFilenet({ quoteId: quote.id });
  };

  // Keyed on server state only (never on isSendVendorQuoteToFilenetLoading) so it can't flash
  // during a send.
  const getTooltipTitle = () => {
    if (isIntegrationMissing) {
      return t('tooltipSendToFileNetIntegrationMissing');
    }

    if (isAlreadySent) {
      return t('tooltipSendToFileNetAlreadySent');
    }

    if (isSendInProgress) {
      return t('tooltipSendToFileNetInProgress');
    }

    return null;
  };

  const tooltipTitle = getTooltipTitle();

  // The send endpoint rejects users without the resource, so self-hide rather than offer a button
  // that always 403s (same idiom as SendAsSpaButton). The caller keeps the alternate-version and
  // Imported-status clauses.
  if (!hasPermission(AccessPermissions.SEND_TO_FILENET)) {
    return null;
  }

  return (
    <Button
      text={t('buttonLabelSendToFileNet')}
      onClick={onClick}
      disabled={isIntegrationMissing || isAlreadySent || isSendVendorQuoteToFilenetLoading}
      variant={
        isIntegrationMissing || isAlreadySent || isSendInProgress ? Variant.OUTLINED : undefined
      }
      tooltipProps={
        tooltipTitle
          ? {
              placement: TooltipPosition.TOP,
              color: Color.NEUTRAL,
              size: Size.SM,
              title: tooltipTitle,
              children: null,
            }
          : undefined
      }
      // MUI swallows hover on disabled buttons; re-enable pointer events so the tooltip shows.
      sx={
        isIntegrationMissing || isAlreadySent
          ? {
              pointerEvents: 'auto !important',
              cursor: 'not-allowed !important',
              '&:hover': { backgroundColor: 'transparent !important' },
            }
          : undefined
      }
    />
  );
};
